import { useNavigate } from "@tanstack/react-router"
import { InfoIcon } from "lucide-react"
import { useEffect, useMemo } from "react"
import type { ControllerRenderProps, FieldValues } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Typography } from "@/components/ui/typography"
import type { RegionDetails } from "@/types/auth"

export function DataRegionField<TFieldValues extends FieldValues>({
  field,
  regionDetails,
  mode,
}: {
  field: ControllerRenderProps<TFieldValues>
  regionDetails?: RegionDetails
  mode: "signin" | "signup"
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Get available regions from regionDetails only (no fallbacks)
  const availableRegions = useMemo(() => {
    return regionDetails?.availableRegions ?? {}
  }, [regionDetails])

  const availableRegionCodes = useMemo(() => Object.keys(availableRegions), [availableRegions])

  // Auto-populate with current region on mount (only if provided in regionDetails)
  useEffect(() => {
    const currentRegion = regionDetails?.region
    if (currentRegion && !field.value) {
      field.onChange(currentRegion)
    }
  }, [field, regionDetails])

  // Handle region change - redirect to new region with appropriate auth page
  const handleRegionChange = (value: string) => {
    field.onChange(value)
    if (availableRegions[value]) {
      const regionUrl = availableRegions[value].url
      const authPath = mode === "signin" ? "/sign-in" : "/sign-up"
      const targetUrl = `${regionUrl}${authPath}`
      navigate({ href: targetUrl })
    }
  }

  return (
    <FormItem>
      <FormLabel>
        {t("auth.common.dataRegionLabel")}
        {availableRegionCodes.length > 0 && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="link" size="sm">
                (What is this? <InfoIcon className="w-4 h-4" />)
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Which region would you like to choose?</DialogTitle>
                <DialogDescription>
                  It is <strong>NOT</strong> possible to change this later.
                </DialogDescription>
                <Separator />
              </DialogHeader>
              {availableRegionCodes.map((regionCode, index) => {
                return (
                  <div key={regionCode}>
                    {index > 0 && <Separator />}
                    <Typography as="h3" className="text-lg font-bold">
                      {t(`auth.countries.withFlag.${regionCode}`)}
                    </Typography>
                    <Typography as="ul">
                      <li>Your data will be stored in {t(`auth.countries.${regionCode}`)}.</li>
                      <li>
                        You will be able to use the service in {t(`auth.countries.${regionCode}`)}.
                      </li>
                    </Typography>
                  </div>
                )
              })}
            </DialogContent>
          </Dialog>
        )}
      </FormLabel>
      <FormControl>
        <Select
          name="dataRegion"
          value={field.value}
          onValueChange={handleRegionChange}
          required
          placeholder={t("auth.common.dataRegionPlaceholder")}
          disabled={availableRegionCodes.length === 0}
        >
          <SelectContent>
            {availableRegionCodes.map((regionCode) => {
              return (
                <SelectItem key={regionCode} value={regionCode}>
                  {t(`auth.countries.withFlag.${regionCode}`)}
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}
